import { trackException } from "@/telemetry/appInsights";
import { api } from "@api/axiosInstance";
import { setUnauthorized } from "@app-redux/slices/userDataSlice";
import { useAppDispatch } from "@app-redux/store";
import { authService } from "@/services/auth";
import { useEffect } from "react";

export const useAuth = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    const response = api.interceptors.response.use(
      (res) => res,
      async (error) => {
        trackException(error instanceof Error ? error : new Error(String(error)));

        const original = error.config;
        if (error.response?.status === 401 && original && !original._retriedAuth) {
          // A 401 means the access token was missing or expired. Re-acquire it (a silent retry, or
          // an interactive redirect only if the session genuinely needs one) and retry the request
          // once. We deliberately never auto-logout here: doing so destroys a still-valid session and
          // shows the confusing Entra "which account do you want to sign out of" screen.
          original._retriedAuth = true;
          const token = await authService.getAccessToken();
          if (token) {
            original.headers = original.headers ?? {};
            original.headers.Authorization = `Bearer ${token}`;
            return api(original);
          }
        }

        if (error.response?.status === 403) {
          dispatch(setUnauthorized());
        }

        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.response.eject(response);
    };
  }, []);
};
